import jwt_decode from 'jwt-decode'

const TOKEN_KEY = 'repasse-medico-token'
const USERNAME_KEY = 'username'
const LOGGED_USER_KEY = 'loggedUser'
const USER_PROFILE = 'profile'
const HOSPITAL = 'hospital'
const HOSPITAL_ID = 'hospital-id'
const MANAGED_HOSPITALS = 'managed-hospitals'
const HOSPITAL_SINCRONIZED_AT = 'hospital-sincronized-at'

export type LoggedUser = {
  id?: string
  name: string
  email: string
  role?: string
}

export const isAuthenticated = () =>
  localStorage.getItem(TOKEN_KEY) !== null && !isTokenExpired()

export const getToken = () => localStorage.getItem(TOKEN_KEY)

export const setToken = (token: string) => {
  localStorage.setItem(TOKEN_KEY, token)
}

export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY)
}

export const getTokenExpirationDate = (token: string) => {
  const decoded: { exp: number | undefined } = jwt_decode(token)

  if (!decoded.exp) return null

  const date = new Date(0)

  date.setUTCSeconds(decoded.exp)

  return date
}

export const isTokenExpired = () => {
  const token = getToken()

  if (!token) return true

  const date = getTokenExpirationDate(token)

  if (date === null || date === undefined) return false

  return !(date.valueOf() > new Date().valueOf())
}

export const getUserId = (): number => {
  const token = getToken()

  const decoded: { sub: string } = jwt_decode(token as string)

  return Number.parseInt(`${decoded.sub}`)
}

export const setUsername = (username: string) => {
  localStorage.setItem(USERNAME_KEY, username)
}

export const getUsername = () => {
  return localStorage.getItem(USERNAME_KEY)
}

/** Fired on window whenever the stored logged user changes. */
export const LOGGED_USER_CHANGED_EVENT = 'logged-user-changed'

export const setLoggedUser = (user: LoggedUser) => {
  localStorage.setItem(LOGGED_USER_KEY, JSON.stringify(user))
  setUsername(user.name)
  window.dispatchEvent(new Event(LOGGED_USER_CHANGED_EVENT))
}

export const getLoggedUser = (): LoggedUser | null => {
  const raw = localStorage.getItem(LOGGED_USER_KEY)

  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as LoggedUser
  } catch {
    return null
  }
}

export const setUserProfile = (profile: string) => {
  localStorage.setItem(USER_PROFILE, profile)
}

export const getUserProfile = (): string | null => {
  return localStorage.getItem(USER_PROFILE)
}

export type StoredManagedHospital = {
  hospitalId: number
  hospital: string
}

export const setHospitalAdmin = (hospital: string) => {
  localStorage.setItem(HOSPITAL, hospital)
}

export const getHospitalAdmin = (): string | null => {
  return localStorage.getItem(HOSPITAL)
}

export const setSelectedHospitalId = (hospitalId: number) => {
  localStorage.setItem(HOSPITAL_ID, String(hospitalId))
}

export const getSelectedHospitalId = (): number | null => {
  const raw = localStorage.getItem(HOSPITAL_ID)

  if (!raw) {
    return null
  }

  const hospitalId = Number.parseInt(raw, 10)

  return Number.isFinite(hospitalId) ? hospitalId : null
}

export const setManagedHospitals = (hospitals: StoredManagedHospital[]) => {
  localStorage.setItem(MANAGED_HOSPITALS, JSON.stringify(hospitals))
}

export const getManagedHospitals = (): StoredManagedHospital[] => {
  const raw = localStorage.getItem(MANAGED_HOSPITALS)

  if (!raw) {
    return []
  }

  try {
    return JSON.parse(raw) as StoredManagedHospital[]
  } catch {
    return []
  }
}

export const setHospitalSynchronizedAt = (epochMs: number) => {
  localStorage.setItem(HOSPITAL_SINCRONIZED_AT, String(epochMs))
}

export const getHospitalSynchronizedAt = (): string | null => {
  return localStorage.getItem(HOSPITAL_SINCRONIZED_AT)
}

export const logout = () => {
  removeToken()
  localStorage.removeItem(USER_PROFILE)
  localStorage.removeItem(USERNAME_KEY)
  localStorage.removeItem(LOGGED_USER_KEY)
  localStorage.removeItem(HOSPITAL)
  localStorage.removeItem(HOSPITAL_ID)
  localStorage.removeItem(MANAGED_HOSPITALS)
  localStorage.removeItem(HOSPITAL_SINCRONIZED_AT)
}

export const parseHospitalSynchronizedAtMs = (
  millis: string | null
): number | null => {
  if (millis == null || millis.trim() === '') {
    return null
  }

  const millisAsNumber = Number(millis.trim())

  return Number.isFinite(millisAsNumber) ? millisAsNumber : null
}
